// src/components/DeliveryModePicker.tsx
//
// "How will your child learn?" — online is open now; hybrid and on-site
// show as Coming soon. On-site opens cluster by cluster, so we also ask
// where the family lives, and parents can ask to be told when a closed
// mode opens near them.

import { useState } from "react";
import type {
  DeliveryMode,
  DeliveryModeOption,
} from "../app/api/enrolmentApi";
import type { ParentLocation } from "../app/api/parentApi";
import { useRegisterOnsiteInterestMutation } from "../app/api/parentApi";

import { Check, Laptop, School, Shuffle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "./Ico";
export const NIGERIAN_STATES = [
  "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue",
  "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu",
  "FCT (Abuja)", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina",
  "Kebbi", "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo",
  "Osun", "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara",
  "Outside Nigeria",
];

const MODE_COPY: Record<DeliveryMode, { icon: LucideIcon; blurb: string }> = {
  online: {
    icon: Laptop,
    blurb:
      "Short video lessons your child does at home, at their own pace within each week. They upload what they made, and a tutor reviews and gives feedback on every piece.",
  },
  hybrid: {
    icon: Shuffle,
    blurb:
      "Online lessons during the week, plus on-site lab days for hardware, team projects and showcases.",
  },
  onsite: {
    icon: School,
    blurb: "The full classroom programme, every week, at a Future Smart Lab centre near you.",
  },
};

const fmt = (n: number | null) =>
  n == null ? "" : `₦${Number(n).toLocaleString("en-NG")}/month`;

export function DeliveryModePicker({
  modes,
  value,
  onChange,
  location,
  interest,
}: {
  modes: DeliveryModeOption[];
  value: DeliveryMode;
  onChange: (m: DeliveryMode) => void;
  location: ParentLocation | null;
  interest: ("hybrid" | "onsite")[];
}) {
  const [register, { isLoading }] = useRegisterOnsiteInterestMutation();
  const [note, setNote] = useState("");

  const toggleInterest = async (mode: "hybrid" | "onsite") => {
    setNote("");
    if (!location) {
      setNote("Tell us where you live first (below), so we know which area to open.");
      return;
    }
    const next = interest.includes(mode)
      ? interest.filter((m) => m !== mode)
      : [...interest, mode];
    try {
      const res = await register({ modes: next }).unwrap();
      setNote(res.message ?? "");
    } catch (err: any) {
      setNote(err?.data?.error ?? "Could not save that — please try again.");
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
        How will your child learn?
      </p>
      {modes.map((m) => {
        const copy = MODE_COPY[m.mode];
        const selected = value === m.mode && m.open;
        const interested = m.mode !== "online" && interest.includes(m.mode as "hybrid" | "onsite");
        return (
          <div
            key={m.mode}
            role={m.open ? "button" : undefined}
            onClick={() => m.open && onChange(m.mode)}
            className={[
              "rounded-[18px] border-2 px-4 py-3 transition",
              m.open ? "cursor-pointer" : "opacity-80",
              selected
                ? "border-[#1b2d5e] bg-[#f1f3f8]"
                : "border-[#dde2ec] bg-white",
            ].join(" ")}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg"><Ico icon={copy.icon} /></span>
              <p className="flex-1 text-sm font-bold text-[#16213e]">{m.label}</p>
              {m.open ? (
                <span className="text-[12.5px] font-bold text-[#4a44b0]">
                  {fmt(m.monthlyFee)}
                </span>
              ) : m.full ? (
                <span className="rounded-full bg-[#fde4e1] px-2 py-0.5 text-[11.5px] font-bold text-[#b3372d]">
                  Full in your area
                </span>
              ) : (
                <span className="rounded-full bg-[#fff8ee] px-2 py-0.5 text-[11.5px] font-bold text-[#7a4a10]">
                  Coming soon
                </span>
              )}
            </div>
            <p className="mt-1 text-[12.5px] leading-5 text-[#5a6278]">{copy.blurb}</p>
            {m.open && m.mode !== "online" && m.seatsLeft != null && (
              <p className="mt-1 text-[12.5px] font-semibold text-[#b26a1d]">
                {m.seatsLeft} place{m.seatsLeft === 1 ? "" : "s"} left in your area
              </p>
            )}
            {!m.open && m.mode !== "online" && (
              <button
                type="button"
                disabled={isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleInterest(m.mode as "hybrid" | "onsite");
                }}
                className={`mt-2 rounded-full px-3 py-1 text-[12.5px] font-semibold ${interested ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#ebeff7] text-[#4a44b0] hover:bg-[#e5e8ef]"}`}
              >
                {interested && <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />}{" "}
                {interested
                  ? m.full ? "We'll tell you when a place opens" : "We'll tell you when it opens near you"
                  : m.full ? "Tell me when a place opens" : "Tell me when it opens near me"}
              </button>
            )}
          </div>
        );
      })}
      {note && <p className="text-[12.5px] font-semibold text-[#5a6278]">{note}</p>}
    </div>
  );
}

export function LocationFields({
  value,
  onChange,
}: {
  value: ParentLocation;
  onChange: (v: ParentLocation) => void;
}) {
  const cls =
    "w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs text-[#16213e] outline-none focus:border-[#1b2d5e]";
  const abroad = value.state === "Outside Nigeria";
  return (
    <div className="space-y-2">
      <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
        Where do you live?
      </p>
      <p className="text-[12.5px] leading-5 text-[#8189a0]">
        We open on-site centres where most of our families are, so this helps us decide where to go next.
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        <select
          className={cls}
          value={value.state}
          onChange={(e) => onChange({ ...value, state: e.target.value })}
        >
          <option value="">State…</option>
          {NIGERIAN_STATES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <input
          className={cls}
          placeholder={abroad ? "Country and city" : "City or town"}
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.target.value })}
        />
        <input
          className={cls}
          placeholder="Area (optional)"
          value={value.area ?? ""}
          onChange={(e) => onChange({ ...value, area: e.target.value })}
        />
      </div>
    </div>
  );
}
